import { useEffect, useState, useMemo } from "react";
import { get, first } from "lodash";
import type { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import type { WroWager } from "@tvg/ts-types/WroWager";
import type { Selections } from "@tvg/ts-types/Selections";
import type { BettingInterests, BIPayouts, BIRunner } from "@tvg/ts-types/Bet";
import type {
  RaceInfoMyBets,
  RaceInfoMyBetsResults,
  Willpay,
  WillpayPayout
} from "@tvg/ts-types/Race";

import { BetSelection } from "@tvg/ts-types/Bet";
import { isFavoriteRunner } from "../utils/general";
import { getWinnerBI } from "../utils/raceDetails";

export interface Props {
  wager: WroWager;
  isMultiRace: boolean;
  raceNumber: number;
  currentRace?: RaceInfoMyBets;
  currentRaceDate: string;
  finalBettedLeg: number;
  showWillPays: boolean;
  allRacesFromTrack: RaceInfoMyBets[];
  currentWagerTypeCode: WagerTypeCodesEnum;
  isActive: boolean;
  results: RaceInfoMyBetsResults[] | null;
  isExotic: boolean;
}

export const getAlternateDetails = (
  biRunner: BettingInterests,
  allRacesFromTrack: RaceInfoMyBets[],
  raceNumberIndex: number
) => {
  const selectedBI = get(
    allRacesFromTrack,
    `${raceNumberIndex}.bettingInterests`,
    []
  ).find((bi: BettingInterests) => bi.biNumber === biRunner.biNumber);

  const runners = (biRunner?.runners || []).map((currentRunner: BIRunner) => ({
    runnerId: currentRunner.runnerId,
    runnerName: currentRunner.horseName,
    saddleColor: selectedBI?.saddleColor,
    numberColor: selectedBI?.numberColor,
    isScratched: currentRunner.scratched,
    isAlternate: true,
    currentOdds: selectedBI?.currentOdds
  }));

  const isWinner = get(biRunner, "isWinner", null);

  return { ...biRunner, isWinner, runners };
};

function useSelections({
  wager,
  isMultiRace,
  raceNumber,
  currentRace,
  currentRaceDate,
  finalBettedLeg,
  showWillPays,
  allRacesFromTrack,
  currentWagerTypeCode,
  isActive,
  isExotic
}: Props): BetSelection[][] {
  const initialSelections = useMemo(
    () =>
      get(wager, "selections.selection", []).map(
        (selection: Selections, selectionIndex: number) => {
          const currentRaceNumberIndex = isMultiRace
            ? +raceNumber - 1 + selectionIndex
            : +raceNumber - 1;

          const alternate = selection.alternateBettingInterest
            ? getAlternateDetails(
                selection.alternateBettingInterest,
                allRacesFromTrack,
                currentRaceNumberIndex
              )
            : null;

          return selection.bettingInterests.map(
            (biRunner: BettingInterests) => {
              const biNumber = get(biRunner, "biNumber", 0);
              const selectedBI = get(
                allRacesFromTrack,
                `${currentRaceNumberIndex}.bettingInterests`,
                []
              ).find(
                (bi: BettingInterests) => bi.biNumber === biRunner.biNumber
              );

              const currentOdds = get(biRunner, "currentOdds", {});
              const runners = get(biRunner, "runners") || [];
              const runnersDetails = runners.map((currentRunner: BIRunner) => ({
                runnerId: currentRunner.runnerId,
                runnerName: currentRunner.horseName,
                saddleColor: selectedBI?.saddleColor,
                numberColor: selectedBI?.numberColor,
                isScratched: currentRunner.scratched,
                isAlternate: false
              }));
              const payouts = get(biRunner, "payouts") || [];

              let isWinner = get(biRunner, "isWinner", null);
              let willPayAmount = 0;
              let isFavorite = false;

              if (!isMultiRace) {
                isFavorite = isFavoriteRunner(
                  currentRaceDate,
                  biNumber,
                  currentRace
                );

                if (payouts.length > 0) {
                  willPayAmount = payouts.reduce(
                    (willPaysAmount: number, currentPayout: BIPayouts) =>
                      willPaysAmount + currentPayout.amount,
                    0
                  );
                }
              } else {
                const finalLegWillPays = get(
                  allRacesFromTrack,
                  `${finalBettedLeg - 1}.willPays`
                );
                const currentLeg = get(
                  allRacesFromTrack,
                  currentRaceNumberIndex
                );

                if (isWinner === null && isActive) {
                  const raceResultsPath = `${currentRaceNumberIndex}.results`;
                  const currentRaceResults = get(
                    allRacesFromTrack,
                    raceResultsPath
                  );
                  if (currentRaceResults && currentRaceResults.runners) {
                    const winnersBI = getWinnerBI(
                      currentRaceResults,
                      (isExotic ? currentRaceNumberIndex : 0) + 1
                    );

                    isWinner =
                      !!winnersBI &&
                      !!winnersBI.find(
                        (winnerBi) => winnerBi.biNumber === biNumber
                      );
                  }
                }

                if (showWillPays && finalLegWillPays) {
                  const willPays = first<Willpay>(
                    finalLegWillPays.filter(
                      (currentWillPays: Willpay) =>
                        currentWillPays.type.code === currentWagerTypeCode
                    )
                  );
                  const willPayDetails = first<WillpayPayout>(
                    (get(willPays, "payouts") || []).filter(
                      (payout: WillpayPayout) =>
                        +payout.bettingInterestNumber === biNumber
                    )
                  );

                  if (willPays && willPayDetails) {
                    willPayAmount =
                      (+willPayDetails.payoutAmount / +willPays.wagerAmount) *
                      +wager.wagerAmount;
                  }
                }

                if (currentLeg) {
                  isFavorite = isFavoriteRunner(
                    currentRaceDate,
                    biNumber,
                    currentLeg
                  );
                }
              }

              return {
                runnerNumber: biNumber,
                runners: runnersDetails,
                alternate,
                willPayAmount,
                payouts,
                currentOdds,
                isFavorite,
                isWinner
              };
            }
          );
        }
      ),
    [
      wager,
      allRacesFromTrack,
      isMultiRace,
      raceNumber,
      currentRaceDate,
      finalBettedLeg,
      showWillPays,
      currentWagerTypeCode,
      isActive,
      isExotic,
      currentRace
    ]
  );

  const [selections, setSelections] =
    useState<BetSelection[][]>(initialSelections);

  useEffect(() => {
    setSelections(initialSelections);
  }, [JSON.stringify(initialSelections)]);

  return selections;
}

export default useSelections;
